// ─────────────────────────────────────────────────────────────────────────────
// VENDORED from the marketing site (magic-motorsports repo root, vehicleTitle.js).
//
// The marketing site imports this from BOTH its Vite frontend and server.js so
// that a card, the detail page and the prerendered bot HTML all print the same
// vehicle name. The auction portal now renders the same dealer-stock listings,
// so it has to agree with them too — a Skyline that reads "Skyline R33 GT-R" on
// importjdmmagic.com must not read "Skyline" here.
//
// Kept as a VERBATIM copy rather than a rewrite: the two files are
// dependency-free ESM, and any divergence would show up as two different names
// for one car. If you change the marketing-site original, re-copy it here.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Display titles for vehicles ("1995 Nissan Skyline R33 GT-R V-Spec").
 *
 * Scraped `carlistings` docs already carry the two things buyers actually
 * search for, just not in `make`/`model`:
 *   - the generation/chassis, in `modelDetail` ("Skyline GT-R_R33 Series")
 *   - the trim, at the head of `grade` ("GT-R V-Spec, Brembo calipers, ...")
 * Both are folded into the title here so cards, the detail page and the
 * prerendered bot HTML all read the same.
 *
 * Curated `currentInventory` docs are left alone — their `model` is parsed
 * out of an admin-written `title` that already includes the submodel, and
 * their `description` is prose, not a grade string.
 *
 * Lives at the repo root next to guides.js / landing.js because it is
 * imported by BOTH the Vite frontend (src/) and server.js. Keep it
 * dependency-free ESM — vehicleNames.js is too, so importing it is safe for
 * the Vite build.
 */

import { normalizeMakeName, normalizeModelName } from './vehicleNames.js';

// Full-width (Ｚ３, ＧＴ－Ｒ) -> ASCII; scraped fields mix both widths.
const toHalfWidth = (str) =>
  str
    // "Land Cruiser８０" -> "Land Cruiser 80". The scraper glues the series
    // number straight onto the model in full-width digits, which then renders
    // as "Land Cruiser 8 0" and vanishes entirely from the slug. An ASCII
    // letter in front is what separates that case from a genuinely full-width
    // name like "Ｚ３Roadster", which must stay "Z3Roadster".
    .replace(/([A-Za-z])([０-９]+)/g, '$1 $2')
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, ' ')
    .replace(/[‘’]/g, "'");

const normalize = (value) => toHalfWidth(String(value || '')).replace(/\s+/g, ' ').trim();

// "Avanté G" -> "Avante G", so accented grades still hit the vocabulary below.
const stripAccents = (str) => str.normalize('NFD').replace(/[̀-ͯ]/g, '');

// Word key used for de-duplication: "GT-R" and "GTR" are the same word.
const wordKey = (word) => word.toLowerCase().replace(/[^a-z0-9]/g, '');

// Only scraped listings have these fields; curated inventory has none of them.
const isScrapedListing = (vehicle) =>
  Boolean(vehicle && (vehicle.listingId || vehicle.modelDetail || vehicle.modelCat));

// ---------------------------------------------------------------------------
// Chassis / generation, from `modelDetail`
// ---------------------------------------------------------------------------

// `modelDetail` is "<model> <generation> Series", but the generation slot is
// also where the scraper parks junk ("Mark II その他", "911 (all)").
const CHASSIS_STOPWORDS = new Set([
  'OTHER', 'OTHERS', 'ALL', 'ETC', 'AND', 'THE', 'SERIES', 'CLASS', 'MODEL',
  'TYPE', 'VAN', 'WAGON', 'SEDAN', 'COUPE', 'TRUCK', 'CUSTOM', 'STD'
]);

// A bare series number resolves to a real chassis code only when the trim (or
// an engine named in the grade) pins down the variant: a "Chaser 100 Series"
// is a JZX100 with a Tourer trim and a GX100 with the 1G-FE, and printing the
// wrong code on a listing is worse than printing none. Where nothing pins it
// down the series number stands on its own, which is the name buyers use for
// "Land Cruiser 80" and "Hilux Surf 130" anyway.
const SERIES_CHASSIS = [
  { model: /Chaser|Mark ?II|Cresta|Crest/i, series: '90', code: 'JZX90', when: /Tourer/i },
  { model: /Chaser|Mark ?II|Cresta|Crest/i, series: '100', code: 'JZX100', when: /Tourer/i },
  { model: /Chaser|Mark ?II|Cresta|Crest/i, series: '100', code: 'GX100', when: /\b1G\b/i },
  { model: /Mark ?II|Verossa/i, series: '110', code: 'JZX110', when: /2\.5|iR-V/i },
  { model: /Supra/i, series: '80', code: 'JZA80' },
  { model: /Aristo/i, series: '160', code: 'JZS161', when: /V300/i },
  { model: /Aristo/i, series: '160', code: 'JZS160', when: /S300/i },
  { model: /Altezza/i, series: '10', code: 'SXE10', when: /RS200/i },
  { model: /Altezza/i, series: '10', code: 'GXE10', when: /AS200/i },
  { model: /Celica/i, series: '160', code: 'ST165', when: /GT-?FOUR/i },
  { model: /Celica/i, series: '180', code: 'ST185', when: /GT-?FOUR/i },
  { model: /Celica/i, series: '200', code: 'ST205', when: /GT-?FOUR/i },
  { model: /MR2/i, series: '20', code: 'SW20' },
  { model: /MR2/i, series: '10', code: 'AW11' },
  // "Sprinter Train" is the scraper's mistranslation of Sprinter Trueno.
  { model: /Levin|Trueno|Train/i, series: '80', code: 'AE86', when: /GT[\s-]?APEX|GTV/i },
  { model: /Levin|Trueno|Train/i, series: '90', code: 'AE92', when: /GT[\s-]?APEX|GTV/i },
  { model: /Levin|Trueno|Train/i, series: '100', code: 'AE101', when: /GT[\s-]?APEX/i },
  { model: /Levin|Trueno|Train/i, series: '110', code: 'AE111', when: /BZ-?[RG]/i },
  { model: /Soarer/i, series: '30', code: 'JZZ30', when: /2\.5/i },
  { model: /Starlet/i, series: '80', code: 'EP82', when: /GT|Turbo/i },
  { model: /Starlet/i, series: '90', code: 'EP91', when: /GT|Turbo/i }
];

const resolveSeriesCode = (vehicle, series) => {
  const model = normalize(`${vehicle.model} ${vehicle.modelDetail}`);
  const hint = stripAccents(normalize(vehicle.grade));
  const match = SERIES_CHASSIS.find(
    (row) => row.series === series && row.model.test(model) && (!row.when || row.when.test(hint))
  );
  return match ? match.code : '';
};

/**
 * Chassis code for a listing, e.g. "R33", "S15", "FD", "JZX100", "80".
 * Returns '' when the generation cannot be read confidently.
 */
export const getChassisCode = (vehicle) => {
  if (!vehicle) return '';
  const detail = normalize(vehicle.modelDetail);
  if (!detail) return '';

  // "Silvia S15 series" -> "Silvia S15"
  let rest = detail.replace(/\s*(series|シリーズ)\s*$/i, '');
  // The scraper glues a trim in front of the code: "Skyline GT-R_R33",
  // "Impreza WRX_GD". The code is always the tail.
  if (rest.includes('_')) rest = rest.slice(rest.lastIndexOf('_') + 1);

  const token = (rest.split(' ').pop() || '').replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
  if (!token || CHASSIS_STOPWORDS.has(token.toUpperCase())) return '';

  // Real codes look like R32 / S15 / FD / JZX100 / E46 / HA1: a leading letter
  // plus a few alphanumerics. A bare 2-3 digit run is a series number, which
  // is either resolvable to a code or worth showing as-is. Leading zeroes are
  // the scraper splitting a list ("Alt H02, 03 series"), and four digits are a
  // date it parked in the generation slot ("2CV6 January 1948").
  let code = '';
  if (/^[1-9][0-9]{1,2}$/.test(token)) {
    code = resolveSeriesCode(vehicle, token) || token;
  } else if (/^[A-Za-z][A-Za-z0-9]{1,5}$/.test(token)) {
    code = token.toUpperCase();
  }
  if (!code) return '';

  // Nothing gained by "Subaru BRZ BRZ".
  const nameWords = new Set(normalize(`${vehicle.make} ${vehicle.model}`).split(' ').map(wordKey));
  if (nameWords.has(wordKey(code))) return '';

  return code;
};

// ---------------------------------------------------------------------------
// Trim, from the head of `grade`
// ---------------------------------------------------------------------------

// `grade` is "<trim>, <feature>, <feature>, ..." — but the separator is
// unreliable ("Grande Power Seats Automatic Climate Control ..."), so the head
// is matched against a closed vocabulary instead of being split on words.
// Longest match wins, so entries do not need to be ordered. An entry is either
// "Name" or ["pattern to match", "name to display"].
const TRIM_NAMES = [
  // Nissan
  'GT-R V-Spec II Nur', 'GT-R V-Spec II', 'GT-R V-Spec N1', 'GT-R V-Spec',
  'GT-R M-Spec Nur', 'GT-R M-Spec', 'GT-R NISMO', 'GT-R Autech Version', 'GT-R',
  'GTS-t Type M', 'GTS-t Type S', ['GTS 25t Type M', 'GTS25t Type M'],
  ['GTS 25t Type S', 'GTS25t Type S'], ['GTS 25t', 'GTS25t'],
  'GTS Type S', 'GTS Type X', 'GTS-4', 'GTS25', 'GTS-t', 'GTS',
  '25GT-X Turbo', '25GT Turbo', '25GT-FOUR', '25GT-V', '25GT-X', '25GT',
  '20GT', '35GT', 'GT-X', 'Autech Version',
  'Spec R Aero', 'Spec R', 'Spec S', "K's Aero", "K's", "Q's", "J's",
  'Type M', 'Type X', 'Type S',
  'Version ST', 'Version NISMO', 'Version S', 'Version T', 'Version R',
  'Version Z', 'NISMO', '300ZX Twin Turbo', '300ZX',
  // Toyota
  'Tourer V', 'Tourer S', 'Tourer',
  'Grande Four Regalia', 'Grande Regalia', 'Grande Four', 'Grande iR-V',
  'Grande Limited', 'Grande',
  'Avante Twin Turbo', 'Avante G', 'Avante',
  'iR-V', 'iR-S', 'RZ-S', 'RZ', 'SZ-R', 'SZ', 'GT-FOUR', 'GT Apex',
  'GT Twin Turbo', 'GT Turbo', 'Aero Top', 'Super Edition', 'V300 Vertex',
  'V300', 'RS200 Z Edition', 'RS200', 'AS200', 'TRD', 'LG Grande', 'LG', 'GL',
  'SS-II', 'SS-I', 'BZ-R', 'BZ-G', 'XZ', 'FZ',
  // Land Cruiser / Prado / Hilux Surf
  'VX Limited', 'VX', 'ZX', 'GX', 'SSR-X Wide', 'SSR-X', 'SSR-G', 'SSR-V',
  'TZ', 'TX', 'EX', 'SR',
  // Cresta / Chaser saloon grades
  'Super Lucent Exceed', 'Super Lucent', 'Exceed G', 'Exceed',
  // Mazda
  'Type RB S-Package', 'Type R Bathurst', 'Type RS-R', 'Type RS', 'Type RB',
  'Type RZ', 'Type R', 'S Special', 'V Special', 'M Package', 'Winning Limited',
  'YS Limited', 'Efini', 'VS',
  // Honda
  'SiR-II', 'SiR', 'VTi', 'Si',
  // Subaru
  'WRX STI Type RA', 'WRX Type RA', 'WRX STI', 'WRX', 'STI Version', 'STI',
  // Mitsubishi
  'GSR', 'VR-4', 'MR',
  // Generic
  '2.0GT Twin Turbo', '2.5GT Twin Turbo', '3.0GT Twin Turbo',
  '2.0GT', '2.5GT', '3.0GT', 'Twin Turbo', 'Turbo', 'GT', 'RS'
];

const escapeRe = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Separators inside a name are flexible ("GTR" / "GT-R" / "GT R" all match) and
// a match may not run into more word characters — "Type RS" must not claim the
// "Type RS-R" of a grade that spells the trim out in full.
const trimMatcher = (pattern, label) => ({
  re: new RegExp(`^${escapeRe(pattern).replace(/[\s-]+/g, '[\\s-]*')}(?![A-Za-z0-9-])`, 'i'),
  label: () => label
});

const TRIM_MATCHERS = [
  // Evo grades read "GSR Evolution IV"; flipping them to "Evolution IV GSR"
  // keeps the title readable for both `model: "Lancer"` and
  // `model: "Lancer Evolution"` (the duplicate word is dropped below).
  {
    re: /^(GSR|RS|MR|GT-A|TME)\s*Evolution\s*(I{1,3}|IV|VI{0,3}|IX|X)(?![A-Za-z0-9])/i,
    label: (m) => `Evolution ${m[2].toUpperCase()} ${m[1].toUpperCase()}`
  },
  {
    re: /^Evolution\s*(I{1,3}|IV|VI{0,3}|IX|X)(\s*(GSR|RS|MR|GT-A|TME))?(?![A-Za-z0-9])/i,
    label: (m) => `Evolution ${m[1].toUpperCase()}${m[3] ? ` ${m[3].toUpperCase()}` : ''}`
  },
  // Displacement-prefixed Toyota grades: "2.5iR-V", "2.0 Tourer V". The
  // listing's own spacing is kept, so "2.0GT" does not become "2.0 GT".
  {
    re: /^(\d\.\d)(\s?)(iR-V|iR-S|iR|Tourer\s[VS]|Tourer|GT-FOUR|GT)(?![A-Za-z0-9-])/i,
    label: (m) => `${m[1]}${m[2]}${m[3]}`
  },
  ...TRIM_NAMES.map((entry) =>
    Array.isArray(entry) ? trimMatcher(entry[0], entry[1]) : trimMatcher(entry, entry))
];

// Where the trim stops and the feature list begins.
const GRADE_HEAD_SPLIT = /[,:;/|+*()[\]、。・･★☆◆◇■□●○◎※\n\r]|\s[-–—]\s/;

// ---------------------------------------------------------------------------
// Free-form trim extraction
// ---------------------------------------------------------------------------
//
// TRIM_NAMES above is a closed vocabulary, and it was written for the Toyota /
// Nissan sports cars this site started with. Against the live feed that covers
// almost nothing: of ~26,300 active listings, 83% match no entry at all, and
// every one of the 409 listings whose grade names an anniversary or limited run
// ("V300 10th Anniversary Special Edition", "GT-R Autech Version 40th
// Anniversary Limited Edition 422 Units") published a title that dropped it.
// There are ~14,100 distinct (model, trim) pairs in the feed, so extending the
// list is not a fix — the tail is far too long to enumerate.
//
// So the default is inverted here: the grade head IS the trim unless it looks
// like something else. The vocabulary still runs first, because it normalizes
// spelling ("GTS 25t" -> "GTS25t") and reorders Evo grades; whatever it leaves
// behind is then taken verbatim if it still reads like a name.

// The head stops being a trim name at the first of these. The feed runs trim
// and equipment together with no separator ("Grande Regalia Pearl Color Full
// Aero Custom CROSS SPEED HYPER EDITION 19-inch Cannon Muffler"), so this is
// the only thing standing between a title and a paragraph.
const TRIM_STOP_WORDS = new Set([
  // drivetrain / running gear
  'engine', 'engines', 'turbocharger', 'intercooler', 'supercharger', 'camshaft',
  'muffler', 'exhaust', 'downpipe', 'manifold', 'intake', 'pipe', 'radiator',
  'suspension', 'coilover', 'coilovers', 'damper', 'dampers', 'spring', 'springs',
  'brake', 'brakes', 'rotor', 'rotors', 'caliper', 'calipers', 'pad', 'pads',
  'clutch', 'flywheel', 'differential', 'lsd', 'gearbox', 'transmission',
  // wheels / tyres
  'wheel', 'wheels', 'aw', 'alloy', 'alloys', 'aluminum', 'aluminium', 'rim',
  'rims', 'tire', 'tires', 'tyre', 'tyres', 'inch', 'inches',
  // body / styling
  'aero', 'spoiler', 'bumper', 'skirt', 'skirts', 'fender', 'fenders', 'bonnet',
  'hood', 'wing', 'lip', 'diffuser', 'overfender', 'rollbar', 'rollcage',
  // lighting / electronics
  'hid', 'led', 'headlight', 'headlights', 'headlamp', 'headlamps', 'fog',
  'lamp', 'lamps', 'taillight', 'taillights', 'navigation', 'navi', 'hdd',
  'dvd', 'cd', 'tv', 'audio', 'stereo', 'speaker', 'speakers', 'bluetooth',
  'camera', 'keyless', 'etc', 'immobilizer', 'alarm',
  // cabin
  'seat', 'seats', 'upholstery', 'dashboard', 'steering', 'pedal', 'pedals',
  'airbag', 'airbags', 'abs', 'conditioner', 'conditioning', 'climate', 'heater',
  'power', 'windows', 'mirror', 'mirrors', 'cruise', 'sunroof', 'moonroof',
  // sales copy / condition
  'mileage', 'odometer', 'record', 'records', 'history', 'certificate',
  'warranty', 'inspection', 'shaken', 'smoking', 'accident', 'owner', 'owners',
  'dealer', 'dealership', 'garage', 'imported', 'import', 'domestic', 'overseas',
  'genuine', 'aftermarket', 'oem', 'included', 'equipped', 'maintenance',
  'delivery', 'condition', 'rust', 'repaint', 'restored', 'km', 'miles',
  'price', 'negotiable', 'sold', 'stock', 'arrival', 'auction',
  'negotiation', 'reserved', 'sale', 'sales', 'undecided', 'pending',
  // gearbox / steering side, spelled out ("5-speed manual", "Left-hand drive")
  'speed', 'manual', 'automatic', 'drive', 'lhd', 'rhd',
  'left', 'right', 'left-hand', 'right-hand', 'lefthand', 'righthand',
  // engine work and body conversions, which are modifications and not grades
  'cam', 'bore', 'bore-up', 'stroker', 'swap', 'swapped', 'conversion',
  'face', 'front-end', 'replica', 'complete', 'kit', 'widebody',
  // Capitalised filler the prose test cannot catch ("Full Original",
  // "Rare Color", "Fully Repainted") — adjectives about condition, not grades.
  'original', 'fully', 'repainted', 'rare', 'specification', 'color', 'colour',
  'beautiful', 'excellent', 'clean', 'pristine', 'mint', 'immaculate',
  // Sales-copy nouns that arrive Title-Cased from machine translation and so
  // sail past the prose test: "New car parallel import" was publishing titles
  // like "1988 Chevrolet Corvette New car". Verified against the live feed —
  // ~700 active listings hit one of these.
  'car', 'cars', 'vehicle', 'vehicles', 'truck', 'trucks', 'new', 'used',
  'one', 'store', 'manager', 'managers', 'recommended', 'recommend',
  'late', 'early', 'normal', 'actual', 'parallel', 'trade', 'year', 'years',
  'paint', 'job', 'refrigerated', 'floor', 'column', 'shift', 'cvt',
  'interior', 'exterior'
]);

// Same job, for shapes a word list cannot express: "19AW", "17inch", "5MT".
// The gearbox alternative is exactly digit + MT/AT — a looser class here
// matched "1st" and "24ST", silently rejecting the ordinal editions and real
// ST trims this change exists to surface.
const TRIM_STOP_RE = /^(\d+(aw|jj|j)|\d+-?(inch|in)|\d-?(speed|spd)|\d[am]t|r\d{2}|\d{3}\/\d{2}|20\d{2})$/i;

// A tail made only of these carries nothing a buyer would search for.
const TRIM_NOISE_RE = /^(base ?(model|grade)?|standard|std|other|others|normal|none|unknown|no grade|full|set|type|grade|model|spec|package|edition|limited|special|custom|deluxe|dx|sdx|gl|gs|le|se|xe|van|truck|wagon|sedan|coupe|hardtop|convertible|cab|dump|long|wide|short|high|low|roof|\d[\d.]*|[a-z]|4wd|2wd|awd|ff|fr|4x4|at|mt|diesel|gasoline|petrol|turbo|na)$/i;

// Leading engine capacity in cc ("660 Wild Wind 4WD") reads as noise. Decimal
// displacements are kept — "4.2 ZX Diesel" is how Land Cruisers are listed.
// The lookahead protects limited-run counts that share these numbers: in
// "1000 Units Limited Edition" the 1000 is the edition size, not an engine.
const LEADING_CC_RE = /^(660|550|1500|2000|3000|1300|1000|1800|2500|4200)\s+(?!(units?|limited|edition)\b)/i;
const DRIVETRAIN_EDGE_RE = /^(4wd|2wd|awd|ff|fr|4x4)\s+|\s+(4wd|2wd|awd|ff|fr|4x4)$/gi;

// Past the third word, only edition vocabulary may continue the name. This is
// what separates "Mini Light Special Memorial Edition" and "40th Anniversary
// Limited Edition 422 Units" — which are the whole point of this change — from
// "GT Apex Full same-color paint finish Watanabe", which is a sentence.
const EDITION_WORD_RE = /^(\d+(st|nd|rd|th)|\d{1,4}|anniversary|edition|editions|limited|special|version|package|model|commemorative|memorial|selection|series|final|units?|spec|type|[ivx]{1,4})$/i;

// Trim names in this feed are translated from Japanese and come back Title
// Cased ("Wild Wind", "Royal Saloon", "Mini Light Special"). A run of ordinary
// lowercase is prose, not a name — that single test catches far more sales copy
// than any blocklist, which is why the list above does not need to be complete.
const isProseWord = (word) =>
  /^[a-z][a-z-]{3,}$/.test(word) && !/^\d+(st|nd|rd|th)$/i.test(word);

const MAX_TRIM_FREE_WORDS = 3;
const MAX_TRIM_WORDS = 6;
const MAX_TRIM_CHARS = 46;

/**
 * Take the leading run of `text` that still reads like a trim name.
 * Returns '' when nothing usable survives.
 */
const extractFreeformTrim = (text) => {
  // An em/en dash with no surrounding spaces survives GRADE_HEAD_SPLIT and is
  // almost always a status banner glued on: "Exceed—Under Negotiation—4WD".
  // A leading possessive is the residue of a vocabulary match that stopped
  // before an apostrophe ("GT's Special ..." matches "GT", leaving "'s ...") —
  // swallowed whole so the bare "s" cannot leak into the title.
  let rest = normalize(text).split(/[—–]/)[0]
    .replace(/^'s(?=\s|$)/i, '')
    .replace(/^[\s'"·・.-]+/, '');
  if (!rest) return '';

  rest = rest.replace(LEADING_CC_RE, '').replace(DRIVETRAIN_EDGE_RE, ' ').trim();
  if (!rest) return '';

  const kept = [];
  for (const word of rest.split(' ')) {
    // Anything non-ASCII at this point is junk glued onto the name — normalize
    // already folded full-width forms and stripAccents ran upstream, so what
    // remains is katakana, ♪, ≪ and friends ("4WD♪", "Ilmシャー"). Stop here
    // rather than skip: what follows a junk marker is never part of the name.
    if (/[^\x20-\x7E]/.test(word)) break;
    // `cased` keeps capitalisation for the prose test below; `bare` is folded
    // for the lookups. Testing the folded form against isProseWord would make
    // every word look lowercase and reject the entire feed.
    const cased = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
    const bare = cased.toLowerCase();
    if (!bare) break;
    // The second lookup folds possessives into the stop list: "Owner's" is
    // 'owners' with an apostrophe, not a new word.
    if (TRIM_STOP_WORDS.has(bare) || TRIM_STOP_WORDS.has(bare.replace(/'/g, '')) || TRIM_STOP_RE.test(bare)) break;
    // Hyphenated compounds carry their stop word inside: "One-owner",
    // "Non-Smoking", "Low-floor" must stop even though the whole token is not
    // in the set.
    if (bare.includes('-') && bare.split('-').some((p) => TRIM_STOP_WORDS.has(p))) break;
    if (isProseWord(cased)) break;
    if (kept.length >= MAX_TRIM_WORDS) break;
    // Beyond the free allowance the name may only continue as an edition.
    if (kept.length >= MAX_TRIM_FREE_WORDS && !EDITION_WORD_RE.test(bare)) break;
    if (kept.join(' ').length + word.length + 1 > MAX_TRIM_CHARS) break;
    // Push with quote marks removed — every test above ran on the stripped
    // token, and pushing the raw word was how '"Iron' and 'Mines"' shipped
    // unbalanced quotes into <title> tags.
    kept.push(word.replace(/"/g, ''));
  }
  if (!kept.length) return '';

  // A dangling count reads as truncation: "...Limited Edition 422" without the
  // "Units" it belonged to. Drop a trailing bare number. Trailing "Full" or
  // "Super" is the same problem in adjective form — the word they modified was
  // cut ("Type II Super..." → "Type II Super") — but only at the tail: leading
  // "Super Custom" and mid-phrase "Full Metal Door" are real names.
  while (
    kept.length > 1 &&
    (/^\d{1,4}$/.test(kept[kept.length - 1]) || /^(full|super)$/i.test(kept[kept.length - 1]))
  ) kept.pop();

  // Trailing connectives read as a sentence cut short ("Limited to", "with").
  while (kept.length && /^(with|and|for|to|of|in|the|a|an|plus)$/i.test(kept[kept.length - 1])) {
    kept.pop();
  }
  // Emphasis punctuation is decoration, and it must not let noise slip the
  // filter below: "Base grade!" is the same non-information as "Base grade".
  const tail = kept.join(' ')
    .replace(DRIVETRAIN_EDGE_RE, ' ')
    .replace(/[!?*~〜。！？\s]+$/u, '')
    .replace(/^[!?*~〜\s]+/u, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!tail || TRIM_NOISE_RE.test(tail)) return '';

  // Needs at least one real word — a lone "G" or "4.2" is not a trim worth
  // printing, but "G Limited" and "4.2 ZX Diesel" are.
  const hasSubstance = tail.split(' ').some((w) => /[a-z]{2,}/i.test(w));
  if (!hasSubstance && tail.split(' ').length < 2) return '';

  return tail;
};

/**
 * Trim/grade name for a listing, e.g. "GT-R V-Spec", "Tourer V", "Spec R",
 * "V300 10th Anniversary Special Edition", "Wild Wind Limited".
 *
 * @param {Object} vehicle
 * @param {Object} [options]
 * @param {boolean} [options.vocabularyOnly=false]  restrict to the closed
 *   TRIM_NAMES vocabulary, ignoring free-form grade text. This is what
 *   buildVehicleSlug uses: titles can gain the full trim immediately, while
 *   the ~26,300 already-indexed canonical URLs stay exactly where they are.
 */
export const getVehicleTrim = (vehicle, { vocabularyOnly = false } = {}) => {
  if (!isScrapedListing(vehicle)) return '';
  const grade = normalize(vehicle.grade);
  if (!grade) return '';

  const head = stripAccents(normalize(grade.split(GRADE_HEAD_SPLIT)[0]));
  if (!head) return '';

  let best = null;
  for (const matcher of TRIM_MATCHERS) {
    const hit = matcher.re.exec(head);
    if (hit && (!best || hit[0].length > best.length)) {
      best = { length: hit[0].length, label: matcher.label(hit) };
    }
  }
  if (vocabularyOnly) return best ? best.label : '';

  // Whatever the vocabulary claimed is authoritative (it normalizes spelling);
  // the remainder of the head is appended when it still reads like a name.
  const matched = best ? best.label : '';
  const tail = extractFreeformTrim(best ? head.slice(best.length) : head);

  return [matched, tail].filter(Boolean).join(' ').trim();
};

// ---------------------------------------------------------------------------
// Title assembly
// ---------------------------------------------------------------------------

// Some scraped models come through lower-cased ("silvia"). Only all-lowercase
// ASCII words are touched, so "RX-7" survives untouched.
const capitalizeModel = (model) =>
  model.replace(/\b[a-z][a-z]+\b/g, (word) => word[0].toUpperCase() + word.slice(1));

// Half the model name is often left in Japanese ("Land Cruiserプラド",
// "ユーノスRoadster"). Untranslated it reads badly and, worse, gets stripped
// out of the slug entirely — a Prado would land on a URL claiming it is a
// plain Land Cruiser.
//
// The tables live in vehicleNames.js, which is also what the /makes pages and
// the sitemap use. Keeping one copy is the point: this file previously carried
// its own shorter list, so a listing could render "Crown Majesta" in the grid
// while the model page it linked to still said "Crownマジェスタ".

// Drop words the title already says: model "Lancer Evolution" + trim
// "Evolution IV GSR" should not render as "Lancer Evolution Evolution IV GSR".
// Exported for server.js, which groups listings into grade pages and needs
// "911 Carrera" under model "911" to fold to "Carrera" exactly as titles do.
export const dropRepeatedWords = (phrase, context) => {
  const seen = new Set(context.split(' ').map(wordKey).filter(Boolean));
  const kept = phrase.split(' ').filter((word) => {
    const key = wordKey(word);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return kept.join(' ');
};

/**
 * Full display title: "1995 Nissan Skyline R33 GT-R V-Spec".
 *
 * @param {Object} vehicle
 * @param {Object} [options]
 * @param {boolean} [options.withYear=true]  prefix the model year
 * @param {boolean} [options.withTrim=true]  append the trim/grade name
 * @param {boolean} [options.vocabularyOnlyTrim=false]  restrict the trim to the
 *   closed vocabulary. Only buildVehicleSlug sets this — see the note there.
 */
export const buildVehicleTitle = (
  vehicle,
  { withYear = true, withTrim = true, vocabularyOnlyTrim = false } = {}
) => {
  if (!vehicle) return 'Unknown Vehicle';

  const make = normalizeMakeName(normalize(vehicle.make));
  const model = capitalizeModel(normalizeModelName(normalize(vehicle.model)));
  const base = `${make} ${model}`.trim();
  const parts = [];

  if (withYear && vehicle.year) parts.push(String(vehicle.year));
  if (base) parts.push(base);

  const chassis = getChassisCode(vehicle);
  if (chassis) parts.push(chassis);

  if (withTrim) {
    const raw = getVehicleTrim(vehicle, { vocabularyOnly: vocabularyOnlyTrim });
    const trim = dropRepeatedWords(raw, `${base} ${chassis}`);
    if (trim) parts.push(trim);
  }

  return parts.join(' ').trim() || 'Unknown Vehicle';
};

/** Make + model + submodel, without the year. */
export const buildVehicleName = (vehicle, options = {}) =>
  buildVehicleTitle(vehicle, { ...options, withYear: false });

/**
 * Coarser trim used to GROUP listings onto a grade page, as opposed to the
 * specific trim printed in a title.
 *
 * The two want opposite things. A title wants maximum specificity — "V300 10th
 * Anniversary Special Edition" is what a buyer types into Google. A grade page
 * wants depth: every V300 variant on one page, not eight pages holding two cars
 * each. Keying pages off the title trim scattered 118 Toyota Aristo listings
 * across 41 buckets, only two of which were deep enough to enter the sitemap,
 * and published "3.0 V300 Vertex Edition" as a rival page to "V300 Vertex".
 *
 * Two coarsenings:
 *   - a leading engine displacement is dropped, so "3.0 V300 Vertex Edition"
 *     groups with "V300 Vertex Edition". The displacement still shows on the
 *     listing itself, which is where it distinguishes one car from another;
 *   - the closed vocabulary wins where it has an entry, folding edition
 *     suffixes into the parent grade ("2.5 25GT Turbo" -> "25GT Turbo").
 *
 * Falls back to the free-form trim when the vocabulary has nothing, which is
 * what keeps "Wild Wind" and "Land Venture" as their own pages. Grouping only —
 * titles and vehicle slugs never call this.
 */
export const getGradeGroupTrim = (vehicle) => {
  if (!vehicle) return '';
  const grade = normalize(vehicle.grade);
  if (!grade) return '';
  // Both lookups run against the displacement-stripped grade. Stripping only
  // for the vocabulary pass left "3.0 S300 Vertex Edition" — whose S300 has no
  // vocabulary entry — falling back to its unstripped self and opening a rival
  // page to "S300 Vertex Edition".
  const stripped = { ...vehicle, grade: grade.replace(/^\d\.\d\s*/, '') };
  return getVehicleTrim(stripped, { vocabularyOnly: true }) || getVehicleTrim(stripped);
};

// Apostrophes close up ("K's" -> "ks") rather than becoming a separator.
const slugify = (text) =>
  stripAccents(normalize(text))
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * URL slug for a listing: "1996-toyota-land-cruiser-80-vx".
 *
 * BOTH tiers must call this: server.js 301s any path that differs from the
 * canonical it computes, so a slug built from a different field set here would
 * bounce every link the SPA renders. That also means the Mongo projections
 * feeding the redirect handlers have to include modelDetail and grade —
 * without them the server computes a shorter slug than the app renders and the
 * two fight.
 *
 * Deliberately pinned to the CLOSED trim vocabulary, so the slug no longer
 * tracks the title exactly. Free-form grade text moved ~19,700 titles at once;
 * letting the slug follow would have re-pointed the same number of indexed
 * canonicals in a single deploy, and a URL is a far weaker ranking signal than
 * a <title>. Flip `vocabularyOnlyTrim` to false here to let URLs catch up once
 * the title change has settled in the index.
 */
export const buildVehicleSlug = (vehicle) => {
  if (!vehicle) return 'vehicle';
  const parts = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  return slugify(buildVehicleTitle(vehicle, { vocabularyOnlyTrim: true }))
    || slugify(parts) || 'vehicle';
};
